import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { Alert, Button, Card, Snackbar } from '@mui/material'
import { filter, isEmpty, map, take } from 'lodash'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChatMessage, Conversation, UserRole } from '../../api'
import { createExperimentForm, historyList, historyRead, markAllNotificationsAsRead } from '../../api/api'
import { getUserNameToken, signOut } from '../../api/token'
import AllExperimentsImg from '../../assets/all_experiments_img.svg'
import UnreadNotifications from '../../assets/bell_active_icon.svg'
import Notifications from '../../assets/bell_icon.svg'
import ChartImg from '../../assets/chart_img.svg'
import HomeLogo from '../../assets/home_logo.svg'
import LearningCenterImg from '../../assets/learning_center_img.svg'
import SparkleGradient from '../../assets/sparkle_gradient.svg'
import { NotificationsMenu } from '../../components/ChatHistory/NotificationsMenu'
import { ActionButton, CreateExperimentButton } from '../../components/common/Button'
import { CustomTooltip } from '../../components/CustomTooltip'
import CustomDialog from '../../components/dialogs/CustomDialog/CustomDialog'
import { RequestAccessDialog } from '../../components/dialogs/RequestAccessDialog'
import { QuestionInput } from '../../components/QuestionInput'
import { SuggestionCardsCarousel } from '../../components/SuggestionCardsCarousel'
import { UserInfoMenu } from '../../components/UserInfoMenu'
import { AgentNames } from '../../constants/agentNames'
import { useMenu } from '../../hooks/useMenu'
import { AppStateContext } from '../../state/AppProvider'
import { getRandomSuggestedQuestions } from '../../utils/questions'
import styles from './Home.module.css'

const Home = () => {
  const appStateContext = useContext(AppStateContext)
  const [userName, setUserName] = useState<string>('')
  const navigate = useNavigate()
  const { anchorEl, open, handleClick, handleClose } = useMenu()
  const chatHistory = appStateContext?.state.chatHistory
  const [recentExperiments, setRecentExperiments] = useState<Conversation[] | null>(null)
  const [isCheckDetailsAccessDialogOpen, setIsCheckDetailsAccessDialogOpen] = useState(false)
  const [isRequestAccessDialogOpen, setIsRequestAccessDialogOpen] = useState(false)
  const isViewer = appStateContext?.state.user?.role === UserRole.VIEWER
  const notifications = appStateContext?.state.notifications
  const hasUnreadNotifications = notifications
    ? filter(notifications, notification => !notification.is_read).length > 0
    : false
  const [anchorNotificationsEl, setAnchorNotificationsEl] = useState<null | HTMLElement>(null)
  const openNotificationsMenu = Boolean(anchorNotificationsEl)

  const handleClickNotifications = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorNotificationsEl(event.currentTarget)
  }

  const handleCloseNotifications = () => {
    setAnchorNotificationsEl(null)
    markAllNotificationsAsRead().then(() => {
      appStateContext?.dispatch({ type: 'MARK_ALL_NOTIFICATIONS_AS_READ' })
    })
  }

  const suggestedQuestions = useMemo(() => {
    const agents = [
      AgentNames.onboarding_assistant,
      AgentNames.methodology_assistant,
      AgentNames.kpi_assistant,
      ...(!isViewer ? [AgentNames.create_experiment_form_filler] : []),
    ]
    return getRandomSuggestedQuestions(agents, 1)
  }, [isViewer])

  const getRecentExperiments = async () => {
    const experiments = await historyList(0, true)
    return experiments
  }

  useEffect(() => {
    getUserNameToken().then(userName => {
      setUserName(userName || '')
    })
    getRecentExperiments().then(experiments => {
      setRecentExperiments(experiments)
    })
    historyList().then(response => {
      if (response) {
        appStateContext?.dispatch({ type: 'FETCH_CHAT_HISTORY', payload: response })
      } else {
        appStateContext?.dispatch({ type: 'FETCH_CHAT_HISTORY', payload: null })
      }
    })
    appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: null })
  }, [])

  const handleClickChatItem = async (convId: string) => {
    historyRead(convId)
      .then(conversation => {
        navigate('/app')
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation })
        appStateContext?.dispatch({ type: 'SET_SHOULD_VALIDATE_FORM', payload: false })
      })
      .catch(err => {
        console.error(`Error fetching chat history for conversation ${convId}`, err)
      })
  }

  const handleSaveHomeChatMessage = async (question: ChatMessage['content']) => {
    appStateContext?.dispatch({ type: 'SET_HOME_CHAT_MESSAGE', payload: question })
    navigate('/app')
  }

  const handleCreateExperiment = async () => {
    const title = new Date().toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    try {
      const conversation = await createExperimentForm(title)
      if (conversation) {
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation })
        appStateContext?.dispatch({ type: 'UPDATE_CHAT_HISTORY', payload: conversation })
        navigate('/app')
      }
    } catch (err) {
      console.error('Error creating experiment', err)
    }
  }

  const handleOpenRequestAccessDialog = () => {
    setIsCheckDetailsAccessDialogOpen(false)
    if (!appStateContext?.state.hasPendingRoleChangeRequest) {
      setIsRequestAccessDialogOpen(true)
    } else if (appStateContext?.state.hasPendingRoleChangeRequest) {
      appStateContext?.dispatch({
        type: 'SET_SNACKBAR_STATE',
        payload: {
          message: 'You already have a pending request. Please wait for it to be processed.',
          severity: 'warning',
        },
      })
    }
  }

  return (
    <div className={styles.homeContainer}>
      <div className={styles.homeNotificationsContainer}>
        <div className={styles.homeLogoContainer}>
          <img
            src={HomeLogo}
            alt="Home Logo"
            className={styles.homeLogo}
          />
        </div>
        <div className={styles.homeHeaderContainerButtons}>
          <div
            className={styles.homeNotificationsIconDiv}
            onClick={(event: React.MouseEvent<HTMLDivElement>) => handleClickNotifications(event)}>
            <img
              src={hasUnreadNotifications ? UnreadNotifications : Notifications}
              className={styles.notificationsIcon}
            />
          </div>
          <NotificationsMenu
            className={styles.notificationsMenu}
            anchorNotificationsEl={anchorNotificationsEl}
            openNotificationsMenu={openNotificationsMenu}
            handleCloseNotifications={handleCloseNotifications}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          />
          <Button
            id="home-header-welcome-container"
            aria-controls="home-header-welcome-container"
            className={styles.homeHeaderWelcomeContainer}
            onClick={handleClick}>
            <span className={styles.homeHeaderWelcomeText}>
              Hello, <b>{userName}</b>
            </span>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </Button>
          <UserInfoMenu
            anchorEl={anchorEl}
            open={open}
            handleClose={handleClose}
            signOut={signOut}
            className={styles.userInfoMenu}
            menuId="home-header-welcome-container"
            ariaLabelledBy="home-header-welcome-container"
          />
        </div>
      </div>
      <div className={styles.homeHeader}>
        <div className={styles.homeTextContainer}>
          <div className={styles.homeTextContainerTitle}>
            Unlock <img src={SparkleGradient} />
          </div>
          <span className={styles.homeTextContainerSubtitle}>Your experimentation potential</span>
        </div>
        <div className={styles.createExperimentButtonContainer}>
          <CustomTooltip
            placement="right-end"
            followCursor
            title={"You don't have permission to create experiments"}
            disableHoverListener={!isViewer}
            disableFocusListener={!isViewer}
            disableInteractive={!isViewer}
            disableTouchListener={!isViewer}>
            <span>
              <CreateExperimentButton
                disabled={isViewer}
                text="Create a New Experiment"
                onClick={handleCreateExperiment}
              />
            </span>
          </CustomTooltip>
          {isViewer && (
            <span
              className={styles.requestEditorAccessText}
              onClick={() => setIsCheckDetailsAccessDialogOpen(true)}>
              Request Editor Access
            </span>
          )}
        </div>
        <div className={styles.homeChatInput}>
          <div className={styles.chatInput}>
            <QuestionInput
              clearOnSend
              placeholder="Ask anything!"
              onSend={async question => {
                await handleSaveHomeChatMessage(question)
              }}
              disabled={false}
            />
          </div>

          <div className={styles.suggestionCardsContainer}>
            <div className={styles.suggestionCardsContainerTitle}>You may ask</div>
            <SuggestionCardsCarousel
              allQuestions={suggestedQuestions}
              onClick={question => handleSaveHomeChatMessage(question)}
            />
          </div>
        </div>
      </div>
      <div className={styles.homeContent}>
        {!(isEmpty(recentExperiments) && isEmpty(chatHistory)) && (
          <div className={styles.homeContentSection}>
            <span className={styles.homeContentSectionTitle}>Recent</span>
            <div className={styles.homeContentSectionContent}>
              {!isEmpty(recentExperiments) && (
                <Card className={styles.homeContentSectionCard}>
                  <span className={styles.homeContentSectionCardTitle}>My Experiments</span>
                  <div className={styles.recentList}>
                    {map(take(recentExperiments, 6), chat => (
                      <span
                        key={chat.id}
                        className={styles.recentListItem}
                        onClick={() => handleClickChatItem(chat.id)}>
                        {chat.title}
                      </span>
                    ))}
                  </div>
                </Card>
              )}
              {!isEmpty(chatHistory) && (
                <Card className={styles.homeContentSectionCard}>
                  <span className={styles.homeContentSectionCardTitle}>Chat History</span>
                  <div className={styles.recentList}>
                    {map(take(chatHistory, 6), chat => (
                      <span
                        key={chat.id}
                        className={styles.recentListItem}
                        onClick={() => handleClickChatItem(chat.id)}>
                        {chat.title}
                      </span>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          </div>
        )}
        <div className={styles.homeContentSection}>
          <span className={styles.homeContentSectionTitle}>Explore More</span>
          <div className={styles.homeContentSectionContent}>
            <Card className={styles.homeContentSectionLinkCard}>
              <img src={AllExperimentsImg} />
              <span className={styles.homeContentSectionLinkCardTitle}>All Experiments</span>
              <span className={styles.homeContentSectionLinkCardDescription}>
                See the complete list with all of what Experimentation at Scale teams have to offer.
              </span>
              <div className={styles.homeContentSectionLinkCardButtonContainer}>
                <ActionButton
                  onClick={() => {
                    navigate('/app/experiments')
                  }}
                  text="Browse Experiments"
                  disabled={false}
                  large={false}
                />
              </div>
            </Card>
            <Card className={styles.homeContentSectionLinkCard}>
              <img src={LearningCenterImg} />
              <span className={styles.homeContentSectionLinkCardTitle}>Learning Center</span>
              <span className={styles.homeContentSectionLinkCardDescription}>
                Explore our library with best practices and learnings for your experiments!
              </span>
              <div className={styles.homeContentSectionLinkCardButtonContainer}>
                <ActionButton
                  onClick={() => {
                    window.open(
                      'https://groups.coca-cola.com/sites/ExperimentationAtScale/SitePages/ES-Learnings.aspx',
                      '_blank',
                    )
                  }}
                  text="Start Learning"
                  disabled={false}
                  large={false}
                />
              </div>
            </Card>
            <Card className={styles.homeContentSectionLinkCard}>
              <img src={ChartImg} />
              <span className={styles.homeContentSectionLinkCardTitle}>Experiments Dashboard</span>
              <span className={styles.homeContentSectionLinkCardDescription}>
                Our interactive chart provides an overview of our experiments data, mapping their value against
                readiness scores, displaying experiments by GLA, OU, and other structural perspectives.
              </span>
              <div className={styles.homeContentSectionLinkCardButtonContainer}>
                <ActionButton
                  onClick={() => {
                    window.open(appStateContext?.state.frontendSettings?.urls?.experiments_dashboard, '_blank')
                  }}
                  text="View Dashboard"
                  disabled={false}
                  large={false}
                />
              </div>
            </Card>
          </div>
        </div>
      </div>
      <div className={styles.homeFooter}>
        <span className={styles.footerText}>© Copyright 2025 Coca-Cola</span>
      </div>
      <Snackbar
        open={appStateContext?.state.snackbarState !== null}
        autoHideDuration={3000}
        onClose={() => appStateContext?.dispatch({ type: 'SET_SNACKBAR_STATE', payload: null })}>
        <Alert
          onClose={() => appStateContext?.dispatch({ type: 'SET_SNACKBAR_STATE', payload: null })}
          severity={appStateContext?.state.snackbarState?.severity}
          classes={{
            colorSuccess: styles.snackbarSuccess,
            colorWarning: styles.snackbarWarning,
          }}>
          {appStateContext?.state.snackbarState?.message}
        </Alert>
      </Snackbar>
      <CustomDialog
        open={isCheckDetailsAccessDialogOpen}
        setClose={() => setIsCheckDetailsAccessDialogOpen(false)}
        title="Access"
        primaryAction={() => setIsCheckDetailsAccessDialogOpen(false)}
        secondaryAction={handleOpenRequestAccessDialog}
        primaryActionButtonText="OK"
        secondaryActionButtonText="Request Access">
        Please check your login details before requesting editor access. You may have access with a different email
        address.
      </CustomDialog>
      <RequestAccessDialog
        open={isRequestAccessDialogOpen}
        onClose={() => setIsRequestAccessDialogOpen(false)}
      />
    </div>
  )
}

export default Home
